import { Box } from '@mui/material';
import { z } from 'zod';

import Heading from '@/features/Dashboard/components/Heading';
import { DrawerStep } from '@/features/Dashboard/drawer/types';
import ColorPaletteSelector from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector';
import { ColorPalette } from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';
import { ItemSelection } from '@/features/Dashboard/form/AppForm/components/ItemSelection';
import { TabName } from '@/features/Dashboard/form/AppForm/components/TabName';
import type { ChartFormValues } from '@/features/Dashboard/form/AppForm/context/types';
import type { FWDChartWidgetV2 } from '@/features/Dashboard/types';
import { AppType } from '@/features/Dashboard/types';

import { FWD_CHART_MAX_ITEMS } from '@/features/Dashboard/constants/const';
import type { FormAppDefinition } from '../types';
import {
  buildChart,
  ColorPaletteSchema,
  graphToFormValues,
  NameSchema,
  SymbolsStateSchema
} from './_helpers';

export const fwdChartDefinition: FormAppDefinition<ChartFormValues, FWDChartWidgetV2> = {
  type: AppType.FWD,
  drawerStep: DrawerStep.SelectFWDChart,
  dashboardControls: { edit: true },
  widgetControls: { export: true },
  maxItems: FWD_CHART_MAX_ITEMS,
  initialValues: {
    name: 'Forward Curves chart',
    type: AppType.FWD,
    symbols: { active: [], backlog: [] },
    colorPalette: ColorPalette.Vibrant
  },
  schema: z.object({
    id: z.string().optional(),
    type: z.literal(AppType.FWD),
    name: NameSchema,
    symbols: SymbolsStateSchema,
    colorPalette: ColorPaletteSchema,
    from: z.string().optional()
  }),
  Form: () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Heading
        subtitle='Set up your chart preferences and data'
        title='Configure forward curves chart'
      />
      <TabName />
      <ItemSelection />
      <ColorPaletteSelector />
    </Box>
  ),
  toWidget: values => buildChart(values),
  canEditWidget: (w): w is FWDChartWidgetV2 =>
    w.type === 'graph' && w.metadata?.graphType === 'forwardSimple',
  fromWidget: w => graphToFormValues(w)
};
